import { ContactType, type Contact } from '@/api/queries/conversations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MessageCircle, MessageSquare, Phone, Trash2, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ContactFormProps {
  onCancel: () => void;
  onSave: (contact: Omit<Contact, 'id' | 'owner_user_id' | 'created_at' | 'updated_at'>) => void;
  onDelete: (contactId: string) => void;
  contact: Contact | null;
  isLoading: boolean;
}

export function ContactForm({ onCancel, onSave, onDelete, contact, isLoading }: ContactFormProps) {
  const { t } = useTranslation('common');
  const { t: tConversations } = useTranslation('conversations');
  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
    contact_type: ContactType.SMS,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name,
        phone_number: contact.phone_number || '',
        contact_type: contact.contact_type,
      });
    } else {
      setFormData({
        name: '',
        phone_number: '',
        contact_type: ContactType.SMS,
      });
    }
    setErrors({});
  }, [contact]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = tConversations('contactForm.errors.nameRequired');
    }

    if (!formData.phone_number.trim()) {
      newErrors.phone_number = tConversations('contactForm.errors.phoneRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSave({
      linked_user_id: contact?.linked_user_id || null,
      name: formData.name.trim(),
      email: null,
      phone_number: formData.phone_number.trim(),
      contact_type: formData.contact_type,
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div>
      {/* Header */}
      <h3 className="font-semibold text-gray-900 pb-4" data-testid="contact-form-title">
        {contact
          ? tConversations('contactForm.editContact', { contactName: contact.name })
          : tConversations('contactForm.addContact')}
      </h3>

      {/* Form */}
      <form onSubmit={handleSubmit} className="flex-1">
        <div className="space-y-6">
          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              {tConversations('contactForm.name')}
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={e => handleInputChange('name', e.target.value)}
                className={`pl-10 ${errors.name ? 'border-red-500' : ''}`}
                placeholder={tConversations('contactForm.namePlaceholder')}
                data-testid="contact-form-name"
                disabled={isLoading}
              />
            </div>
            {errors.name && (
              <p className="mt-2 text-sm text-red-600" data-testid="contact-form-name-error">
                {errors.name}
              </p>
            )}
          </div>

          {/* Phone Field */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              {tConversations('contactForm.phone')}
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
              <Input
                id="phone"
                type="tel"
                value={formData.phone_number}
                onChange={e => handleInputChange('phone_number', e.target.value)}
                className={`pl-10 ${errors.phone_number ? 'border-red-500' : ''}`}
                placeholder={tConversations('contactForm.phonePlaceholder')}
                data-testid="contact-form-phone"
                disabled={isLoading}
              />
            </div>
            {errors.phone_number && (
              <p className="mt-2 text-sm text-red-600" data-testid="contact-form-phone-error">
                {errors.phone_number}
              </p>
            )}
          </div>

          {/* Contact Type Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {tConversations('contactForm.contactType')}
            </label>
            <div className="flex gap-3">
              <Button
                type="button"
                onClick={() => handleInputChange('contact_type', ContactType.SMS)}
                variant="outline"
                className={`flex-1 ${
                  formData.contact_type === ContactType.SMS
                    ? 'text-white bg-gray-700'
                    : 'text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100'
                }`}
                data-testid="contact-form-type-sms"
                disabled={isLoading}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                {tConversations('contactForm.types.sms')}
              </Button>
              <Button
                type="button"
                onClick={() => handleInputChange('contact_type', ContactType.WHATSAPP)}
                variant="outline"
                className={`flex-1 ${
                  formData.contact_type === ContactType.WHATSAPP
                    ? 'text-white bg-gray-700'
                    : 'text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100'
                }`}
                data-testid="contact-form-type-whatsapp"
                disabled={isLoading}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                {tConversations('contactForm.types.whatsapp')}
              </Button>
            </div>
          </div>
        </div>
      </form>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4">
        {/* Left side - Delete button (only when editing) */}
        <div>
          {contact && (
            <Button
              type="button"
              onClick={() => onDelete(contact.id)}
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
              data-testid="contact-form-delete"
              disabled={isLoading}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {t('buttons.delete')}
            </Button>
          )}
        </div>

        {/* Right side - Cancel and Save buttons */}
        <div className="flex items-center gap-3">
          <Button
            type="button"
            onClick={onCancel}
            variant="outline"
            data-testid="contact-form-cancel"
            disabled={isLoading}
          >
            {t('buttons.cancel')}
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            className="!bg-orange-500 text-white hover:text-white hover:bg-orange-600"
            data-testid="contact-form-save"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                {t('buttons.saving')}
              </>
            ) : (
              t('buttons.save')
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
